import type { InputState } from './InputTypes'
import { getSensitivityFactor } from './sensitivity'

export class DesktopInput {
  private mouseX: number = 0
  private mouseY: number = 0
  private boosting: boolean = false
  private disposed: boolean = false
  private hasPointerAim: boolean = false

  private readonly onMouseMove: (e: MouseEvent) => void
  private readonly onMouseDown: (e: MouseEvent) => void
  private readonly onMouseUp: (e: MouseEvent) => void
  private readonly onKeyDown: (e: KeyboardEvent) => void
  private readonly onKeyUp: (e: KeyboardEvent) => void

  constructor(
    private canvas: HTMLCanvasElement
  ) {
    const rect = this.canvas.getBoundingClientRect()
    this.mouseX = rect.left + rect.width / 2
    this.mouseY = rect.top + rect.height / 2

    this.onMouseMove = (e: MouseEvent): void => {
      this.mouseX = e.clientX
      this.mouseY = e.clientY
      this.hasPointerAim = true
    }

    this.onMouseDown = (e: MouseEvent): void => {
      if (e.button === 0) {
        this.boosting = true
      }
    }

    this.onMouseUp = (e: MouseEvent): void => {
      if (e.button === 0) {
        this.boosting = false
      }
    }

    this.onKeyDown = (e: KeyboardEvent): void => {
      if (e.code === 'Space') {
        e.preventDefault()
        this.boosting = true
      }
    }

    this.onKeyUp = (e: KeyboardEvent): void => {
      if (e.code === 'Space') {
        this.boosting = false
      }
    }

    this.bind()
  }

  private bind(): void {
    this.canvas.addEventListener('mousemove', this.onMouseMove)
    this.canvas.addEventListener('mousedown', this.onMouseDown)
    // Fallback if canvas doesn't receive mousemove due to overlays or focus
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
  }

  getState(): InputState {
    if (!this.hasPointerAim) {
      return {
        desiredHeading: 0,
        boost: this.boosting,
        hasAim: false,
        strength: 0,
        distancePx: 0,
      }
    }

    const factor = getSensitivityFactor()
    const rect = this.canvas.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const scaledX = centerX + (this.mouseX - centerX) * factor
    const scaledY = centerY + (this.mouseY - centerY) * factor
    const dx = scaledX - centerX
    const dy = scaledY - centerY
    return {
      desiredHeading: Math.atan2(dy, dx),
      boost: this.boosting,
      hasAim: true,
      strength: 1,
      distancePx: Math.sqrt(dx * dx + dy * dy),
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true

    this.canvas.removeEventListener('mousemove', this.onMouseMove)
    this.canvas.removeEventListener('mousedown', this.onMouseDown)
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }
}
